// Constants and configuration for reports

export const REPORT_TABS = [
  // Basic Setup - Cover Page now includes date range & site
  { id: 'cover-page', name: 'Cover Page & Project Info', icon: '📋', description: 'Project details & reporting period', category: 'setup' },
  { id: 'contents', name: 'Contents', icon: '📑', description: 'Table of contents with page numbers', category: 'setup' },
  { id: 'photos', name: '1.0 Progress Photos', icon: '📸', description: 'Choose & organize photos', category: 'setup' },
  
  // Report Structure
  { id: 'executive-summary', name: '2.0 Executive Summary', icon: '📈', description: 'High-level overview', category: 'structure' },
  { id: 'project-overview', name: '3.0 Project Overview & Scope', icon: '🏗️', description: 'Project description & scope', category: 'structure' },
  
  // Progress Content
  { id: 'progress-highlights', name: '4.0 Progress Highlights', icon: '🌟', description: 'Key progress points', category: 'progress' },
  { id: 'work-completed', name: '5.0 Work Completed This Period', icon: '✅', description: 'Completed activities', category: 'progress' },
  { id: 'quality-inspections', name: '6.0 Quality Inspections & Standards', icon: '🔍', description: 'Quality control measures', category: 'progress' },
  { id: 'safety-compliance', name: '7.0 Safety Compliance & Incidents', icon: '🛡️', description: 'Safety measures & incidents', category: 'progress' },
  
  // Analysis
  { id: 'schedule-performance', name: '8.0 Schedule Performance Analysis', icon: '⏱️', description: 'Timeline analysis', category: 'analysis' },
  { id: 'resource-utilization', name: '9.0 Resource Utilization Report', icon: '👥', description: 'Resource usage analysis', category: 'analysis' },
  { id: 'budget-analysis', name: '10.0 Budget Analysis & Financial Status', icon: '💰', description: 'Financial performance', category: 'analysis' },
  { id: 'risk-assessment', name: '11.0 Risk Assessment & Mitigation', icon: '⚠️', description: 'Risk management', category: 'analysis' },
  
  // Documentation
  { id: 'progress-comparisons', name: '12.0 Progress Comparisons (Before/After)', icon: '↔️', description: 'Visual comparisons', category: 'documentation' },
  { id: 'technical-drawings', name: '13.0 Technical Drawings & Plans', icon: '📐', description: 'Engineering documentation', category: 'documentation' },
  
  // Planning
  { id: 'next-phase', name: '14.0 Next Phase Planning', icon: '🚀', description: 'Future work planning', category: 'planning' },
  { id: 'appendices', name: '15.0 Appendices & Additional Documentation', icon: '📎', description: 'Supporting documents', category: 'planning' }
];

export const REPORT_CATEGORIES = [
  { id: 'setup', name: 'Setup', icon: '⚙️' },
  { id: 'structure', name: 'Report Structure', icon: '🏗️' },
  { id: 'progress', name: 'Progress & Quality', icon: '📊' },
  { id: 'analysis', name: 'Analysis & Performance', icon: '📈' },
  { id: 'documentation', name: 'Documentation', icon: '📁' },
  { id: 'planning', name: 'Planning & Additional Documentation', icon: '🎯' }
];

export const REPORT_TYPES = [
  {
    id: 'monthly-progress',
    name: 'Progress Reports',
    description: 'Photo-based progress documentation with work descriptions and comprehensive reporting',
    icon: '📊',
    color: 'blue'
  },
  {
    id: 'weekly-summary',
    name: 'Work Diary',
    description: 'Comprehensive work diary, activity logs and completion tracking',
    icon: '📅',
    color: 'green'
  },
  {
    id: 'site-attendance',
    name: 'Attendance + Task Completion',
    description: 'Attendance patterns, hours worked, and presence analytics',
    icon: '👥',
    color: 'purple'
  },
  {
    id: 'task-completion',
    name: 'Task Completion Report',
    description: 'Task progress, completion rates, and productivity metrics',
    icon: '✅',
    color: 'emerald'
  },
  {
    id: 'expense-budget',
    name: 'Expense & Budget Report',
    description: 'Financial overview, expense tracking, and budget analysis',
    icon: '💰',
    color: 'yellow'
  }
];

// Sections that can never be excluded from the final report
export const LOCKED_SECTIONS = ['cover-page'];

export const REPORT_PRESETS = [
  { id: 'full', name: 'Full report', included: null },
  { id: 'photos-only', name: 'Photos only', included: ['cover-page', 'contents', 'photos'] }
];

export const DEFAULT_REPORT_SECTIONS = {
  coverPage: {
    client: '',
    contractor: '', // Will be populated from organization settings
    reportAuthor: '',
    reportDate: '',
    projectStatus: 'In Progress', // Default status
    signature: ''
  },
  executiveSummary: {
    overview: '',
    keyMetrics: '',
    majorAchievements: '',
    challenges: '',
    recommendations: ''
  },
  projectOverview: {
    projectDescription: '',
    scope: '',
    objectives: '',
    timeline: '',
    stakeholders: ''
  },
  progressHighlights: {
    completedMilestones: '',
    currentActivities: '',
    upcomingWork: '',
    percentComplete: ''
  },
  workCompleted: {
    activities: '',
    deliverables: '',
    manHours: '',
    materials: '',
    equipment: ''
  },
  qualityInspections: {
    inspections: '',
    standards: '',
    compliance: '',
    defects: '',
    corrective: ''
  },
  safetyCompliance: {
    incidents: '',
    measures: '',
    training: '',
    compliance: '',
    improvements: ''
  },
  schedulePerformance: {
    planned: '',
    actual: '',
    variance: '',
    delays: '',
    recovery: ''
  },
  resourceUtilization: {
    labor: '',
    equipment: '',
    materials: '',
    efficiency: '',
    optimization: ''
  },
  budgetAnalysis: {
    approved: '',
    committed: '',
    actual: '',
    variance: '',
    forecast: ''
  },
  riskAssessment: {
    identified: '',
    probability: '',
    impact: '',
    mitigation: '',
    contingency: ''
  },
  progressComparisons: {
    before: '',
    after: '',
    analysis: '',
    improvements: ''
  },
  nextPhase: {
    activities: '',
    timeline: '',
    resources: '',
    dependencies: '',
    risks: ''
  },
  keyAchievements: {
    milestones: '',
    awards: '',
    innovations: '',
    successes: ''
  },
  technicalDrawings: {
    drawings: '',
    revisions: '',
    approvals: '',
    specifications: ''
  },
  appendices: {
    documents: '',
    references: '',
    calculations: '',
    certifications: ''
  }
};